import {
  CrewClaimTypeEnum,
  CrewMerchantUi,
  InventoryBehavior,
} from 'corso-types';
import { ReactElement, ReactNode } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

export type Variant = CrewMerchantUi.StoreProduct['variants'][number];
export type SelectedVariant = Omit<Variant, 'quantity' | 'id'> & {
  quantity: number;
};

export type PropsWithNonElementChildren<P = unknown> = P & {
  children?: Exclude<ReactNode, ReactElement> | undefined;
};

// --------- Automation

export type Automation = CrewMerchantUi.StoreRule;
export const automation = CrewMerchantUi.storeRuleSchema;

export const automationFormDataSchema = automation
  .pick({
    name: true,
    hook: true,
    isEnabled: true,
  })
  .merge(automation.shape.rule.pick({ event: true }))
  .merge(
    z.object({
      booleanExpression: z.union([z.literal('all'), z.literal('any')]),
      conditions: z.union([
        automation.shape.rule.shape.conditions.options[0].shape.all,
        automation.shape.rule.shape.conditions.options[1].shape.any,
      ]),
    }),
  );

export type AutomationCreate = CrewMerchantUi.StoreRuleCreate;
export type AutomationUpdate = CrewMerchantUi.StoreRuleUpdate;
export const automationUpdate = CrewMerchantUi.storeRuleUpdateSchema;

export type ClaimLineItemCustomField = CrewMerchantUi.AppliedCustomField;
export type CustomField = CrewMerchantUi.CustomField;
export const customField = CrewMerchantUi.customFieldSchema;

export type CustomFieldCreate = CrewMerchantUi.CustomFieldCreate;
export const { customFieldCreateSchema } = CrewMerchantUi;
export type CustomFieldUpdate = CrewMerchantUi.CustomFieldUpdate;
export const { customFieldUpdateSchema } = CrewMerchantUi;

export const returnLocation = CrewMerchantUi.returnLocationSchema;
export type ReturnLocation = CrewMerchantUi.ReturnLocation;

export const returnLocationCreate = CrewMerchantUi.returnLocationCreateSchema;
export type ReturnLocationCreate = CrewMerchantUi.ReturnLocationCreate;

export const claimReason = CrewMerchantUi.claimReasonSchema;
export type ClaimReason = CrewMerchantUi.ClaimReason;
export const claimReasonCreate = CrewMerchantUi.claimReasonCreateSchema;
export type ClaimReasonCreate = CrewMerchantUi.ClaimReasonCreate;
export type ClaimReasonUpdate = CrewMerchantUi.ClaimReasonUpdate & {
  category: ClaimReason['category'];
};

export const { storeUserSignUpAndOnboardSchema, storeUserSignUpSchema } =
  CrewMerchantUi;

export const inventoryBehaviorLabels = {
  [InventoryBehavior.bypass]: 'Do Not Decrement Inventory',
  [InventoryBehavior.decrementIgnoringPolicy]:
    'Decrement Inventory (Ignore Policies)',
  [InventoryBehavior.decrementObeyingPolicy]:
    'Decrement Inventory (Obey Policies)',
} satisfies Record<InventoryBehavior, string>;

export type RegistrationUpdate = CrewMerchantUi.RegistrationUpdate;
export const registrationUpdate = CrewMerchantUi.registrationUpdateSchema;

export const ACCESS_CODE_KEY = CrewMerchantUi.MERCHANT_APP_JWT_QUERY_KEY;

export type StoreUser = CrewMerchantUi.StoreUser;

export type ClaimReasonGroup = CrewMerchantUi.ClaimReasonGroup;
export type ClaimReasonGroupCreate = CrewMerchantUi.ClaimReasonGroupCreate;
export type ClaimReasonGroupUpdate = CrewMerchantUi.ClaimReasonGroupUpdate;

export type ClaimType = CrewClaimTypeEnum;

export type ConfigSettings = CrewMerchantUi.SettingsConfig;
export type ThemeSettings = CrewMerchantUi.SettingsTheme;
export type IntegrationSettings = CrewMerchantUi.SettingsIntegration;

export type ProductGroup = CrewMerchantUi.StoreProductGroup;
export const productGroupSchema = CrewMerchantUi.storeProductGroupSchema;

export type ProductGroupCreate = CrewMerchantUi.StoreProductGroupCreate;
export const productGroupCreateSchema =
  CrewMerchantUi.storeProductGroupCreateSchema;

export const { apiClientRespSchema } = CrewMerchantUi;

const {
  email,
  notifications,
  returns, // applies to all resolution methods
  // refunds, // * not used, as refundValidityDays is the only property and it'd be omitted and overwritten
  giftCards, // displayed as store credit
  variantExchanges,
  warranties,
  registrations,
  shippingProtection,
  orderEditing,
  tracking,
} = CrewMerchantUi.settingsConfigSchema.shape;

export const themeSettingsFormSchema = CrewMerchantUi.settingsThemeSchema;
export const emailSettingsFromSchema = email;
export const notificationSettingsFormSchema = notifications;

export const warrantySettingsFormSchema = warranties;
export const trackingSettingsFormSchema = tracking;

export const RegistrationsFormSchema = registrations.extend({
  theme: themeSettingsFormSchema.pick({
    identifyProductUrl: true,
    identifyProductUrlButtonText: true,
    identifyProductDetailText: true,
    identifyProductFormPlaceholder: true,
    orderLookupRegistrationText: true,
    registrationSubmissionText: true,
    isRegistrationPhoneRequired: true,
    registrationCountryCodes: true,
    emailMarketingConsentText: true,
    emailMarketingConsentDescriptionText: true,
    emailMarketingConsentPolicyUrlTextTitle: true,
  }),
});

export const OrderEditingFormSchema = orderEditing.extend({
  theme: themeSettingsFormSchema.pick({
    orderEditDescriptionText: true,
    orderEditExpiredDescriptionText: true,
  }),
});

export type ProtectionRate = CrewMerchantUi.ProtectionRate;
export type ProtectionRateCreate = CrewMerchantUi.ProtectionRateCreate;
export const { protectionRateCreateSchema, protectionRateUpdateSchema } =
  CrewMerchantUi;

export const shipProtectSettingsFormSchema = shippingProtection;

export const returnSettingsFormSchema = z.object({
  returns,
  refunds: z.discriminatedUnion('isRefundsEnabled', [
    z.object({
      isRefundsEnabled: z.literal(true),
      refundValidityDays: z.number().positive(),
    }),
    z.object({
      isRefundsEnabled: z.literal(false),
      refundValidityDays: z.literal(0),
    }),
  ]),

  giftCards,
  // * have to separate this out because Zod doesn't support nested discriminated unions
  giftCardsControl: z.discriminatedUnion('isGiftCardEnabled', [
    z.object({
      isGiftCardEnabled: z.literal(true),
      giftCardValidityDays: z.number().positive(),
    }),
    z.object({
      isGiftCardEnabled: z.literal(false),
      giftCardValidityDays: z.literal(0),
    }),
  ]),
  variantExchanges: z
    .discriminatedUnion('isExchangeEnabled', [
      z.object({
        isExchangeEnabled: z.literal(true),
        variantExchangeValidityDays: z.number().positive(),
      }),
      z.object({
        isExchangeEnabled: z.literal(false),
        variantExchangeValidityDays: z.literal(0),
      }),
    ])
    .and(variantExchanges.omit({ variantExchangeValidityDays: true })),
});

// TODO make `DEFAULT` into `default` instead to match shadcn/ui variant pattern
export type UIStatusVariant =
  | 'DEFAULT'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info';

export type UpsertFormProps<
  Create extends Record<string, unknown>,
  Update extends { id: number | string },
  Values extends Update = Update,
> = {
  show: boolean;
  onClose: () => void;
  onSubmit: (upsert: Create | Update) => void;
  values?: Values;
};

export type UseFormTypes<
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any -- needed to be able to extract any form type from the generic while restricting to useForm return types
  T extends (...args: any[]) => UseFormReturn<any, any, any>,
> = T extends (
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  ...args: any[]
) => UseFormReturn<infer FieldValues, infer Context, infer TransformedValues>
  ? {
      FieldValues: FieldValues;
      Context: Context;
      TransformedValues: TransformedValues;
    }
  : 'Invalid Form Type';
